import { useRef, useState, type DragEvent, type ReactNode } from 'react'
import { BUTTON_INTERACTIVE_CLASSES } from './Button'

/** Minimal shape of an already-selected file, enough to render a preview/name without depending on any app-specific "UploadedFile" type. */
export interface FileDropZoneCurrentFile {
  name: string
  mimeType: string
  dataUrl: string
}

/** Default styling for the idle drop zone — replaced entirely (not merged) when `dropzoneClassName` is provided. */
const DEFAULT_DROPZONE_CLASSES =
  'border-pax-purple/30 hover:border-pax-blue text-pax-gray hover:text-pax-blue flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-sm transition-colors focus-visible:outline-none'

/** Extra classes merged onto the drop zone while a file is being dragged over it. */
const DEFAULT_DROPZONE_ACTIVE_CLASSES = 'border-pax-blue bg-pax-blue/5 text-pax-blue'

/** Default styling for the "current file" card shown once a file has been selected. */
const DEFAULT_CURRENT_FILE_CLASSES =
  'border-pax-purple/30 flex items-center gap-3 rounded-xl border bg-white p-3'

/**
 * Default "Remove" button styling — mirrors the `danger`/`sm` preset from `apps/main`'s local
 * `Button` component (this package's own `Button` has no `danger` variant), so switching to
 * {@link FileDropZone} doesn't change how it looks by default.
 */
const DEFAULT_REMOVE_BUTTON_CLASSES =
  'inline-flex items-center justify-center rounded-lg border border-pax-red/30 bg-transparent px-3 py-1 text-xs text-pax-red transition-colors hover:bg-pax-red/5 focus-visible:outline-none'

function UploadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
    </svg>
  )
}

/** Props for {@link FileDropZone}. */
export interface FileDropZoneProps {
  /** Label rendered above the drop zone / current-file card. */
  label?: ReactNode
  /** Icon rendered inside the idle drop zone, before `uploadLabel`. Defaults to a built-in upload glyph — override to match a caller's existing visual (size, stroke, color). */
  icon?: ReactNode
  /** MIME type filter forwarded to the underlying `<input type="file">` (e.g. `"image/*"`). */
  accept?: string
  /** The currently selected file, if any. When set, a "current file" card replaces the drop zone. */
  current?: FileDropZoneCurrentFile
  /** Show an image thumbnail in the current-file card when `current.mimeType` starts with `"image/"`. */
  preview?: boolean
  /**
   * Called with the newly selected/dropped native `File` — via click-to-browse or drag-and-drop.
   * May return a `Promise`; a rejection is caught and rendered as an error message below the
   * field (see `uploadFailedLabel`).
   */
  onFileSelected: (file: File) => void | Promise<void>
  /** Called when the user removes the current file. Omit to hide the remove control. */
  onRemove?: () => void
  disabled?: boolean
  /**
   * Content of the drop zone's call-to-action when idle.
   * Pass a translated string from the app (shared packages must not hardcode user-facing copy).
   * Defaults to `"Upload file"` only for package unit tests / storybook.
   */
  uploadLabel?: ReactNode
  /** Content shown instead of `uploadLabel` while a file is being dragged over the zone. */
  dragActiveLabel?: ReactNode
  /** Label for the remove control. Defaults to `"Remove"`. */
  removeLabel?: ReactNode
  /** Fallback error message shown when `onFileSelected` throws/rejects without an `Error` message. */
  uploadFailedLabel?: string
  /** Classes for the outer wrapper. */
  className?: string
  /** Classes for the idle drop zone. Replaces the default entirely (not merged). */
  dropzoneClassName?: string
  /** Extra classes merged onto the drop zone while a file is being dragged over it. */
  dropzoneActiveClassName?: string
  /** Classes for the "current file" card. Replaces the default entirely (not merged). */
  currentFileClassName?: string
  /** Classes for the remove button. Replaces the default entirely (not merged). */
  removeButtonClassName?: string
}

/**
 * Shared file upload field: a click-to-browse + drag-and-drop zone that swaps to a "current
 * file" card (with optional image preview and a remove control) once a file is selected.
 *
 * Reused everywhere the app lets users upload a single file (event logo, poster/flyer, etc.) so
 * drag-and-drop and styling stay consistent. Every visual piece is parameterizable via the
 * `*ClassName` props so callers can keep their existing look while gaining drag-and-drop.
 */
export function FileDropZone({
  label,
  icon,
  accept,
  current,
  preview,
  onFileSelected,
  onRemove,
  disabled,
  uploadLabel = 'Upload file',
  dragActiveLabel,
  removeLabel = 'Remove',
  uploadFailedLabel = 'Upload failed.',
  className = '',
  dropzoneClassName = DEFAULT_DROPZONE_CLASSES,
  dropzoneActiveClassName = DEFAULT_DROPZONE_ACTIVE_CLASSES,
  currentFileClassName = DEFAULT_CURRENT_FILE_CLASSES,
  removeButtonClassName = DEFAULT_REMOVE_BUTTON_CLASSES,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)
  const [isDragActive, setIsDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFiles(fileList: FileList | null) {
    const file = fileList?.[0]
    if (!file) return
    setError(null)
    try {
      await onFileSelected(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : uploadFailedLabel)
    }
  }

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    dragDepth.current += 1
    setIsDragActive(true)
  }

  function handleDragOver(e: DragEvent<HTMLElement>) {
    // Required for `onDrop` to fire at all — browsers block drops by default.
    e.preventDefault()
    e.stopPropagation()
  }

  function handleDragLeave(e: DragEvent<HTMLElement>) {
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDragActive(false)
  }

  function handleDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current = 0
    setIsDragActive(false)
    if (disabled) return
    void handleFiles(e.dataTransfer.files)
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && <p className="text-pax-black text-sm font-medium">{label}</p>}

      {current ? (
        <div className={currentFileClassName}>
          {preview && current.mimeType.startsWith('image/') && (
            <img src={current.dataUrl} alt="" className="h-10 w-10 rounded-lg object-contain" />
          )}
          <span className="text-pax-black flex-1 truncate text-sm">{current.name}</span>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className={`${BUTTON_INTERACTIVE_CLASSES} ${removeButtonClassName}`}
            >
              {removeLabel}
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={disabled}
          aria-label={typeof uploadLabel === 'string' ? uploadLabel : undefined}
          className={`${BUTTON_INTERACTIVE_CLASSES} ${dropzoneClassName} ${
            isDragActive ? dropzoneActiveClassName : ''
          }`}
        >
          {icon ?? <UploadIcon />}
          {isDragActive && dragActiveLabel ? dragActiveLabel : uploadLabel}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="hidden"
        onChange={(e) => {
          void handleFiles(e.target.files)
          e.target.value = ''
        }}
      />

      {error && <p className="text-pax-red text-xs">{error}</p>}
    </div>
  )
}
